import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import versionChecker from '../../utils/versionCheck';
import TimezoneFixTool from '../admin/TimezoneFixTool';

// Version Management GraphQL
const GET_CURRENT_VERSION = gql`
  query GetCurrentVersion {
    getCurrentVersion {
      version
      buildTime
      lastUpdated
    }
  }
`;

const UPDATE_VERSION = gql`
  mutation UpdateVersion($version: String!, $updateMessage: String) {
    updateVersion(version: $version, updateMessage: $updateMessage) {
      success
      message
    }
  }
`;

const TRIGGER_CACHE_BUST = gql`
  mutation TriggerCacheBust {
    triggerCacheBust {
      success
      message
    }
  }
`;

// ========== JOB OPTIMIZATION FEATURE FLAGS ==========

const GET_JOB_OPTIMIZATION_ENABLED = gql`
  query GetJobOptimizationEnabled {
    isJobOptimizationEnabled
  }
`;

const SET_JOB_OPTIMIZATION_ENABLED = gql`
  mutation SetJobOptimizationEnabled($enabled: Boolean!) {
    setJobOptimizationEnabled(enabled: $enabled)
  }
`;

const GET_JOB_BATCH_PROCESSING_ENABLED = gql`
  query GetJobBatchProcessingEnabled {
    isJobBatchProcessingEnabled
  }
`;

const SET_JOB_BATCH_PROCESSING_ENABLED = gql`
  mutation SetJobBatchProcessingEnabled($enabled: Boolean!) {
    setJobBatchProcessingEnabled(enabled: $enabled)
  }
`;

const GET_DEFERRED_RESERVATION_UPDATES_ENABLED = gql`
  query GetDeferredReservationUpdatesEnabled {
    isDeferredReservationUpdatesEnabled
  }
`;

const SET_DEFERRED_RESERVATION_UPDATES_ENABLED = gql`
  mutation SetDeferredReservationUpdatesEnabled($enabled: Boolean!) {
    setDeferredReservationUpdatesEnabled(enabled: $enabled)
  }
`;

function DeveloperToolsSection() {
  // ========== VERSION MANAGEMENT STATE ==========
  const [newVersion, setNewVersion] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');
  const [isUpdatingVersion, setIsUpdatingVersion] = useState(false);
  const [isTriggeringCacheBust, setIsTriggeringCacheBust] = useState(false);

  // ========== JOB OPTIMIZATION FEATURE FLAGS STATE ==========
  const [jobOptimizationEnabled, setJobOptimizationEnabled] = useState(false);
  const [jobBatchProcessingEnabled, setJobBatchProcessingEnabled] = useState(false);
  const [deferredReservationUpdatesEnabled, setDeferredReservationUpdatesEnabled] = useState(false);

  // ========== VERSION MANAGEMENT QUERIES ==========
  const { data: currentVersionData, refetch: refetchVersion } = useQuery(GET_CURRENT_VERSION);

  // ========== JOB OPTIMIZATION FEATURE FLAGS QUERIES ==========
  const { data: jobOptimizationEnabledData } = useQuery(GET_JOB_OPTIMIZATION_ENABLED);
  const { data: jobBatchProcessingEnabledData } = useQuery(GET_JOB_BATCH_PROCESSING_ENABLED);
  const { data: deferredReservationUpdatesEnabledData } = useQuery(GET_DEFERRED_RESERVATION_UPDATES_ENABLED);

  // ========== VERSION MANAGEMENT MUTATIONS ==========
  const [updateVersion] = useMutation(UPDATE_VERSION, {
    onCompleted: (data) => {
      if (data.updateVersion.success) {
        toast.success(data.updateVersion.message);
        refetchVersion();
        setNewVersion('');
        setUpdateMessage('');
      } else {
        toast.error(data.updateVersion.message);
      }
      setIsUpdatingVersion(false);
    },
    onError: (error) => {
      toast.error(`Failed to update version: ${error.message}`);
      setIsUpdatingVersion(false);
    }
  });

  const [triggerCacheBust] = useMutation(TRIGGER_CACHE_BUST, {
    onCompleted: (data) => {
      if (data.triggerCacheBust.success) {
        toast.success(data.triggerCacheBust.message);
      } else {
        toast.error(data.triggerCacheBust.message);
      }
      setIsTriggeringCacheBust(false);
    },
    onError: (error) => {
      toast.error(`Failed to trigger cache bust: ${error.message}`);
      setIsTriggeringCacheBust(false);
    }
  });

  // ========== JOB OPTIMIZATION FEATURE FLAGS MUTATIONS ==========
  const [updateJobOptimizationEnabled, { loading: updatingJobOptimization }] = useMutation(SET_JOB_OPTIMIZATION_ENABLED, {
    onCompleted: () => {
      toast.success(`Job optimization ${jobOptimizationEnabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      console.error('Failed to update job optimization setting:', error);
      toast.error('Failed to update job optimization setting');
      setJobOptimizationEnabled(!jobOptimizationEnabled);
    }
  });

  const [updateJobBatchProcessingEnabled, { loading: updatingJobBatchProcessing }] = useMutation(SET_JOB_BATCH_PROCESSING_ENABLED, {
    onCompleted: () => {
      toast.success(`Job batch processing ${jobBatchProcessingEnabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      console.error('Failed to update job batch processing setting:', error);
      toast.error('Failed to update job batch processing setting');
      setJobBatchProcessingEnabled(!jobBatchProcessingEnabled);
    }
  });

  const [updateDeferredReservationUpdatesEnabled, { loading: updatingDeferredUpdates }] = useMutation(SET_DEFERRED_RESERVATION_UPDATES_ENABLED, {
    onCompleted: () => {
      toast.success(`Deferred reservation updates ${deferredReservationUpdatesEnabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      console.error('Failed to update deferred reservation updates setting:', error);
      toast.error('Failed to update deferred reservation updates setting');
      setDeferredReservationUpdatesEnabled(!deferredReservationUpdatesEnabled);
    }
  });

  // ========== JOB OPTIMIZATION FEATURE FLAGS EFFECTS ==========
  useEffect(() => {
    if (jobOptimizationEnabledData?.isJobOptimizationEnabled !== undefined) {
      setJobOptimizationEnabled(jobOptimizationEnabledData.isJobOptimizationEnabled);
    }
  }, [jobOptimizationEnabledData]);

  useEffect(() => {
    if (jobBatchProcessingEnabledData?.isJobBatchProcessingEnabled !== undefined) {
      setJobBatchProcessingEnabled(jobBatchProcessingEnabledData.isJobBatchProcessingEnabled);
    }
  }, [jobBatchProcessingEnabledData]);

  useEffect(() => {
    if (deferredReservationUpdatesEnabledData?.isDeferredReservationUpdatesEnabled !== undefined) {
      setDeferredReservationUpdatesEnabled(deferredReservationUpdatesEnabledData.isDeferredReservationUpdatesEnabled);
    }
  }, [deferredReservationUpdatesEnabledData]);

  // ========== VERSION MANAGEMENT HANDLERS ==========
  const handleUpdateVersion = async () => {
    if (!newVersion.trim()) {
      toast.error('Please enter a version number');
      return;
    }

    setIsUpdatingVersion(true);
    try {
      await updateVersion({
        variables: {
          version: newVersion.trim(),
          updateMessage: updateMessage.trim() || undefined
        }
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  const handleTriggerCacheBust = async () => {
    setIsTriggeringCacheBust(true);
    try {
      // First trigger the backend cache bust (increments version)
      await triggerCacheBust();
      
      // Then immediately trigger cache clear on the frontend
      toast.success('Cache bust triggered! Clearing cache and reloading...');
      
      // Small delay to show the toast, then force cache clear
      setTimeout(async () => {
        try {
          // PWA: Send message to service worker to force cache clear
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            try {
              navigator.serviceWorker.controller.postMessage({
                type: 'FORCE_CACHE_CLEAR'
              });
            } catch (error) {
              // Silent error handling
            }
          }

          // PWA: Unregister service worker to force fresh installation
          if ('serviceWorker' in navigator) {
            try {
              const registrations = await navigator.serviceWorker.getRegistrations();
              await Promise.all(
                registrations.map(registration => {
                  return registration.unregister();
                })
              );
            } catch (error) {
              // Silent error handling
            }
          }

          // Clear all browser caches
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
              cacheNames.map(cacheName => {
                return caches.delete(cacheName);
              })
            );
          }

          // Clear local storage (except authentication)
          const authToken = localStorage.getItem('authToken');
          const refreshToken = localStorage.getItem('refreshToken');
          
          localStorage.clear();
          sessionStorage.clear();
          
          // Restore auth tokens
          if (authToken) localStorage.setItem('authToken', authToken);
          if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

          // Force reload with cache busting
          const url = new URL(window.location.href);
          url.searchParams.set('_cacheBust', Date.now());
          url.searchParams.set('_forceReload', 'true');
          
          window.location.href = url.href;
        } catch (error) {
          window.location.reload(true);
        }
      }, 1000);
      
    } catch (error) {
      // Error handled in onError callback
    } finally {
      setIsTriggeringCacheBust(false);
    }
  };

  // ========== JOB OPTIMIZATION FEATURE FLAGS HANDLERS ==========
  const handleToggleJobOptimization = async () => {
    const newState = !jobOptimizationEnabled;
    setJobOptimizationEnabled(newState);
    
    try {
      await updateJobOptimizationEnabled({
        variables: {
          enabled: newState
        }
      });
    } catch (error) {
      console.error('Error toggling job optimization:', error);
    }
  };

  const handleToggleJobBatchProcessing = async () => {
    const newState = !jobBatchProcessingEnabled;
    setJobBatchProcessingEnabled(newState);
    
    try {
      await updateJobBatchProcessingEnabled({
        variables: {
          enabled: newState
        }
      });
    } catch (error) {
      console.error('Error toggling job batch processing:', error);
    }
  };

  const handleToggleDeferredReservationUpdates = async () => {
    const newState = !deferredReservationUpdatesEnabled;
    setDeferredReservationUpdatesEnabled(newState);
    
    try {
      await updateDeferredReservationUpdatesEnabled({
        variables: {
          enabled: newState
        }
      });
    } catch (error) {
      console.error('Error toggling deferred reservation updates:', error);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Developer Tools</h2>
      </div>
      
      <div className="space-y-6">
        {/* Version Management */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3">🚀 Version Management</h3>
          <p className="text-sm text-gray-500 mb-4">
            Update the app version and trigger cache busting to force users to get the latest version.
          </p>
          
          {/* Current Version Display */}
          {currentVersionData?.getCurrentVersion && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900">Current Version</h4>
              <div className="text-sm text-blue-700 mt-1">
                <p><strong>Version:</strong> {currentVersionData.getCurrentVersion.version}</p>
                <p><strong>Last Updated:</strong> {new Date(currentVersionData.getCurrentVersion.lastUpdated).toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Version Update Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Version Number
              </label>
              <input
                type="text"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="e.g., 1.49"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use semantic versioning (e.g., 1.49, 1.50, 2.0)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Update Message (Optional)
              </label>
              <textarea
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                placeholder="Describe what's new in this version..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleUpdateVersion}
                disabled={isUpdatingVersion || !newVersion.trim()}
                className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingVersion ? 'Updating...' : 'Update Version'}
              </button>

              <button
                onClick={handleTriggerCacheBust}
                disabled={isTriggeringCacheBust}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTriggeringCacheBust ? 'Triggering...' : 'Force Cache Bust'}
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="text-sm font-medium text-green-900">📋 How it works for your employees:</h4>
            <div className="text-xs text-green-700 mt-1 space-y-1">
              <p><strong>✅ Simple Process:</strong> When you update the version, employees will automatically see a friendly update popup</p>
              <p><strong>🎯 One Click Update:</strong> Employees just click "Update Now" - no technical knowledge required</p>
              <p><strong>🔄 Automatic:</strong> The popup appears when they open the app or return to it</p>
              <p><strong>⚡ Fast:</strong> Updates take just a few seconds and don't interrupt their work</p>
            </div>
          </div>
          
          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900">🔧 Technical Details:</h4>
            <div className="text-xs text-blue-700 mt-1 space-y-1">
              <p><strong>Update Version:</strong> Changes the version number and triggers update notifications</p>
              <p><strong>Force Cache Bust:</strong> Advanced option to immediately clear all cached data</p>
            </div>
          </div>
        </div>

        {/* Version Check Debugging */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3">🔧 Version Check Debugging</h3>
          <p className="text-sm text-gray-500 mb-4">
            Test and debug the version checking system to ensure update notifications are working properly.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <button
              onClick={() => {
                console.log('=== VERSION CHECKER DEBUG INFO ===');
                console.log('Current version:', versionChecker.currentVersion);
                console.log('Stored version:', localStorage.getItem('app_version'));
                console.log('Last shown version:', localStorage.getItem('last_shown_update_version'));
                console.log('Version check disabled:', localStorage.getItem('version_check_disabled'));
                console.log('Has shown update notice:', versionChecker.hasShownUpdateNotice);
                console.log('Is checking version:', versionChecker.isCheckingVersion);
                toast.success('Debug info logged to console');
              }}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Debug Info
            </button>
            
            <button
              onClick={() => {
                versionChecker.checkNow();
                toast.success('Version check triggered');
              }}
              className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Check Now
            </button>
            
            <button
              onClick={() => {
                versionChecker.testUpdateNotification();
                toast.success('Test notification shown');
              }}
              className="px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Test Popup
            </button>
            
            <button
              onClick={() => {
                versionChecker.reset();
                toast.success('Version checker reset');
              }}
              className="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Reset State
            </button>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900">🛠️ Debugging Tools:</h4>
            <div className="text-xs text-blue-700 mt-1 space-y-1">
              <p><strong>Debug Info:</strong> Shows current state in browser console</p>
              <p><strong>Check Now:</strong> Manually trigger version check</p>
              <p><strong>Test Popup:</strong> Show test update notification</p>
              <p><strong>Reset State:</strong> Clear all localStorage and reset checker state</p>
            </div>
          </div>
        </div>
        {/* Job Optimization Features */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3">🚀 Performance Optimization Features</h3>
          <p className="text-sm text-gray-500 mb-4">
            Advanced features to optimize job creation and processing during Streamline synchronization.
          </p>
          
          <div className="space-y-4">
            {/* Job Optimization Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Enable Job Optimization
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Uses cached service types and optimized batch processing for 80% faster job creation
                </p>
              </div>
              <button
                type="button"
                onClick={handleToggleJobOptimization}
                disabled={updatingJobOptimization}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2
                  ${jobOptimizationEnabled ? 'bg-sky-600' : 'bg-gray-200'}
                  ${updatingJobOptimization ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                role="switch"
                aria-checked={jobOptimizationEnabled}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${jobOptimizationEnabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            {/* Job Batch Processing Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Enable Batch Processing
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Processes multiple jobs simultaneously instead of sequentially
                </p>
              </div>
              <button
                type="button"
                onClick={handleToggleJobBatchProcessing}
                disabled={updatingJobBatchProcessing}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2
                  ${jobBatchProcessingEnabled ? 'bg-sky-600' : 'bg-gray-200'}
                  ${updatingJobBatchProcessing ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                role="switch"
                aria-checked={jobBatchProcessingEnabled}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${jobBatchProcessingEnabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            {/* Deferred Reservation Updates Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Enable Deferred Updates
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Defers expensive reservation update operations during bulk sync for better performance
                </p>
              </div>
              <button
                type="button"
                onClick={handleToggleDeferredReservationUpdates}
                disabled={updatingDeferredUpdates}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2
                  ${deferredReservationUpdatesEnabled ? 'bg-sky-600' : 'bg-gray-200'}
                  ${updatingDeferredUpdates ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                role="switch"
                aria-checked={deferredReservationUpdatesEnabled}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${deferredReservationUpdatesEnabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
          </div>
          
          {/* Warning Box */}
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="text-sm font-medium text-yellow-900">⚠️ Beta Features</h4>
            <div className="text-xs text-yellow-700 mt-1 space-y-1">
              <p><strong>Impact:</strong> Can improve sync performance by 70-80% but requires testing</p>
              <p><strong>Recommendation:</strong> Enable in development first, then staging, then production</p>
              <p><strong>Rollback:</strong> Can be disabled instantly if issues occur</p>
            </div>
          </div>
          
          {/* Performance Indicators */}
          {jobOptimizationEnabled && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-sm font-medium text-green-900">✅ Optimization Active</h4>
              <div className="text-xs text-green-700 mt-1">
                <p>Job creation is now using optimized batch processing with cached service types</p>
                <p>Auto-assignment will be processed separately after job creation for better performance</p>
              </div>
            </div>
          )}
        </div>

        {/* Timezone Date Fix Tool */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3">🕐 Timezone Date Fix Tool</h3>
          <p className="text-sm text-gray-500 mb-4">
            Fix jobs that have incorrect dates due to timezone conversion issues from bulk assignments.
          </p>
          
          <TimezoneFixTool />
        </div>

        {/* Future Developer Tools */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3">🔧 Additional Tools</h3>
          <p className="text-sm text-gray-500 mb-4">
            More developer tools will be available here in future updates.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Database Monitoring</h4>
              <p className="text-xs text-gray-500 mt-1">Coming soon - Real-time database performance metrics</p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">API Analytics</h4>
              <p className="text-xs text-gray-500 mt-1">Coming soon - Streamline API call analytics and optimization</p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Cache Management</h4>
              <p className="text-xs text-gray-500 mt-1">Coming soon - Manual cache invalidation and monitoring</p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Feature Flags</h4>
              <p className="text-xs text-gray-500 mt-1">Coming soon - Advanced feature flag management</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeveloperToolsSection; 